import { useEffect, useRef } from "react";
import {
  buildSubscribeUrl,
  fetchSseTicket,
  journeyTopic,
} from "@/modules/common/api/sse";
import { EnvironmentVariables } from "@/modules/common/types";
import useFeatureFlags from "@/modules/common/hooks/use-feature-flags.hook";
import { FeatureFlagsKeys } from "@/modules/common/context/feature-flags.constants";

const MAX_TOPICS = 100;
const DEBOUNCE_MS = 400;
const BACKOFF_MS = [1000, 2000, 5000];
// Stay fully dormant on instances without an SSE gateway configured (no ticket fetch, no
// EventSource, no reconnect loop). The feature activates automatically once REACT_APP_SSE_GATEWAY_URL
// is set for that instance.
const SSE_ENABLED = Boolean(EnvironmentVariables.SSE_GATEWAY_URL);

export interface JourneyOnCallEvent {
  type: "oncall";
  payload: { journeyUid: string; isOnCall: boolean };
}

interface Params {
  orgUid?: string;
  visibleJourneyUids: string[];
  onEvent: (event: JourneyOnCallEvent) => void;
  enabled?: boolean;
}

export function useJourneyUpdatesSse({
  orgUid,
  visibleJourneyUids,
  onEvent,
  enabled = true,
}: Params): void {
  const sourceRef = useRef<EventSource | null>(null);
  const subscribedKeyRef = useRef<string>("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffIdxRef = useRef(0);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // Runtime kill-switch: stays dormant unless the SseInCallIndicator flag is on for this user.
  const { hasFlag } = useFeatureFlags();
  const flagEnabled = hasFlag(FeatureFlagsKeys.SSE_IN_CALL_INDICATOR);

  const topics = orgUid
    ? [...new Set(visibleJourneyUids)]
        .slice(0, MAX_TOPICS)
        .map((uid) => journeyTopic(orgUid, uid))
    : [];
  const desiredKey = topics.slice().sort().join("|");

  useEffect(() => {
    if (!SSE_ENABLED || !flagEnabled || !enabled || !orgUid) return;

    const teardown = () => {
      sourceRef.current?.close();
      sourceRef.current = null;
    };

    const connect = async () => {
      if (!topics.length) {
        teardown();
        subscribedKeyRef.current = "";
        return;
      }
      try {
        const ticket = await fetchSseTicket(topics);
        teardown();
        const es = new EventSource(buildSubscribeUrl(ticket));
        es.onmessage = (e) => {
          try {
            const parsed = JSON.parse(e.data) as JourneyOnCallEvent;
            if (parsed?.type === "oncall") onEventRef.current(parsed);
          } catch {
            /* ignore non-JSON / pings */
          }
        };
        es.onerror = () => {
          teardown();
          const delay =
            BACKOFF_MS[Math.min(backoffIdxRef.current, BACKOFF_MS.length - 1)];
          backoffIdxRef.current += 1;
          debounceRef.current = setTimeout(connect, delay);
        };
        backoffIdxRef.current = 0;
        sourceRef.current = es;
        subscribedKeyRef.current = desiredKey;
      } catch {
        const delay =
          BACKOFF_MS[Math.min(backoffIdxRef.current, BACKOFF_MS.length - 1)];
        backoffIdxRef.current += 1;
        debounceRef.current = setTimeout(connect, delay);
      }
    };

    if (desiredKey === subscribedKeyRef.current && sourceRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(connect, DEBOUNCE_MS);

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        teardown();
        subscribedKeyRef.current = "";
      } else if (desiredKey) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(connect, DEBOUNCE_MS);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredKey, enabled, orgUid, flagEnabled]);
}
